import { IDENTITY_ERROR_CODES } from './IdentityErrorCode';

/** The three reasons a magic link can be unusable. The UI never receives an HTTP status code. */
export const MAGIC_LINK_ERROR_CODES = [
  IDENTITY_ERROR_CODES.LINK_EXPIRED,
  IDENTITY_ERROR_CODES.LINK_COMPLETED,
  IDENTITY_ERROR_CODES.LINK_INVALID,
] as const;

export type MagicLinkErrorCode = typeof MAGIC_LINK_ERROR_CODES[number];

/**
 * A magic link that cannot be used, as a domain error.
 *
 * It is identified by its `name` and not by a class, because class names are renamed in production
 * builds. It carries no subject: a failed consume has no account to be tied to yet.
 */
export type MagicLinkError = Error & {
  readonly name: 'MagicLinkError';
  readonly code: MagicLinkErrorCode;
};

/** HTTP status to domain code, following the fixed contract of the consume endpoint. */
const CONSUME_STATUS_TO_CODE: Record<number, MagicLinkErrorCode> = {
  400: IDENTITY_ERROR_CODES.LINK_INVALID,
  401: IDENTITY_ERROR_CODES.LINK_EXPIRED,
  409: IDENTITY_ERROR_CODES.LINK_COMPLETED,
};

/** Creates a MagicLinkError for a known code. */
export const createMagicLinkError = (code: MagicLinkErrorCode): MagicLinkError => {
  const error = new Error(`Magic link is not usable: ${code}`) as MagicLinkError;

  return Object.assign(error, { name: 'MagicLinkError' as const, code });
};

/**
 * Converts an already translated domain code into a MagicLinkError, and returns `null` when the
 * code is not about the link.
 *
 * Every endpoint except the consume one uses this, and the HTTP status is ignored on purpose: a
 * `400` from the password endpoint means the policy was not met, and reporting it as a broken link
 * would tell the user the wrong thing.
 */
export const magicLinkErrorFromCode = (domainCode?: string): MagicLinkError | null =>
  domainCode && (MAGIC_LINK_ERROR_CODES as readonly string[]).includes(domainCode)
    ? createMagicLinkError(domainCode as MagicLinkErrorCode)
    : null;

/**
 * Converts a failure of the consume endpoint into a MagicLinkError, or `null` when it is not about
 * the link — a network failure and a 5xx are not a broken link.
 *
 * This is the only place where the HTTP status may decide the code, because it is the only endpoint
 * whose contract fixes one code per status. A code in the body still wins. The fallback costs three
 * lines and is what keeps a response with no envelope from leaving the user on a blank screen.
 */
export const magicLinkErrorFromConsumeResponse = (status: number, domainCode?: string): MagicLinkError | null =>
  magicLinkErrorFromCode(domainCode) ??
  (CONSUME_STATUS_TO_CODE[status] ? createMagicLinkError(CONSUME_STATUS_TO_CODE[status]) : null);

/** Narrows an unknown value to MagicLinkError. Use it instead of `instanceof`. */
export const isMagicLinkError = (value: unknown): value is MagicLinkError =>
  value instanceof Error && (value as MagicLinkError).name === 'MagicLinkError';
